import React from 'react';
import { motion } from 'framer-motion';
import Header from '../../organisms/Header/Header';
import Constants from '../../../app/utils/constants';
import cryptonaryBackground from '../../../assets/images/cryptonary/bg1.webp';
import cryptonaryNumber1 from '../../../assets/images/cryptonary/1.webp';
import cryptonaryNumber2 from '../../../assets/images/cryptonary/2.webp';
import cryptonaryNumber3 from '../../../assets/images/cryptonary/3.webp';
import colors from '../../theme/color';

const cryptonaryNumberImages: Record<string, string> = {
  '1': cryptonaryNumber1,
  '2': cryptonaryNumber2,
  '3': cryptonaryNumber3,
};

const HeroSection = () => {
  const projectHighlights = Constants.cryptonaryProjectHighlights;

  return (
    <section
      className="relative w-full overflow-hidden md:pb-15 lg:pb-25 xl:pb-40"
      style={{ backgroundColor: colors.cryptonaryHeroBase }}
    >
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute -left-[5%] top-[-8%] z-10 h-[400px] w-[400px] rounded-full opacity-[0.25] blur-[120px] lg:h-[650px] lg:w-[650px] lg:opacity-[0.28] lg:blur-[180px]"
          style={{ backgroundColor: colors.cryptonaryAccentGreen }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: colors.cryptonaryHeroBase }} />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle_at_18%_38%,${colors.cryptonaryHeroRadialA}57 0%,${colors.cryptonaryHeroRadialB}6B 26%,${colors.cryptonaryHeroBase}33 52%,${colors.cryptonaryHeroBase}00 76%)`,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(180deg,${colors.cryptonaryHeroLinear}42 0%,${colors.cryptonaryHeroLinear}2E 54%,${colors.cryptonaryHeroLinear}00 100%)`,
          }}
        />
        <div className="absolute -right-[2%] top-[-1%] h-[86%] w-[98vw] sm:-right-[3%] sm:top-[-2%] sm:h-[90%] sm:w-[96vw] md:right-0 md:top-0 md:h-[100%] md:w-[102vw] lg:right-0 lg:top-0 lg:h-[96%] lg:w-[104vw] xl:right-0 xl:top-0 xl:h-[90%] xl:w-[106vw] 2xl:right-0 2xl:top-0 2xl:h-[78%] 2xl:w-[82vw] min-[1440px]:top-[-10%] min-[1440px]:h-[56%] min-[1440px]:w-[76vw] min-[2560px]:top-[-8%] min-[2560px]:h-[52%] min-[2560px]:w-[68vw]">
          <img
            src={cryptonaryBackground}
            alt=""
            className="h-full w-full -translate-x-[2%] object-contain object-right-top mix-blend-lighten opacity-[0.74] md:translate-x-[6%] lg:translate-x-[8%] xl:translate-x-[7%] 2xl:translate-x-[4%] min-[1440px]:translate-x-[2%] min-[2560px]:translate-x-[0%]"
            style={{
              WebkitMaskImage:
                'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 10%, rgba(0,0,0,0.72) 19%, rgba(0,0,0,0.94) 26%, rgba(0,0,0,1) 33%, rgba(0,0,0,1) 100%)',
              maskImage:
                'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 10%, rgba(0,0,0,0.72) 19%, rgba(0,0,0,0.94) 26%, rgba(0,0,0,1) 33%, rgba(0,0,0,1) 100%)',
            }}
          />
        </div>
      </div>

      <div className="relative w-full">
        <Header />
      </div>

      <div className="relative mx-auto mt-14 w-full max-w-[1380px] px-5 md:mt-18 md:px-8 lg:mt-30 lg:max-w-[1680px] lg:px-10 xl:max-w-[1820px] xl:px-12 2xl:max-w-[1960px] 2xl:px-16">
        <div className="max-w-[980px] lg:max-w-[980px] xl:max-w-[1040px] 2xl:max-w-[1120px] text-center md:text-start">
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.25 }}
            transition={{ duration: 1.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-rajdhani text-[45px] font-bold uppercase leading-[0.9] tracking-tight text-white sm:text-[55px] md:text-[75px] lg:text-[5.5rem] xl:text-[6.7rem]"
          >
            Cryptonary
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.25 }}
            transition={{ duration: 1.7, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 max-w-[1000px] font-gilroy-medium text-[18px] leading-[1.14] text-white sm:text-[18px] md:text-[28px] lg:text-[35px] xl:text-[2.9rem]"
          >
            Simplifying Crypto Markets with Intelligent Data &amp; Analytics
          </motion.p>
        </div>

        <div className=" mt-24 grid grid-cols-2 md:grid-cols-3 gap-y-7 gap-x-10 px-5 md:px-10 lg:px-0 md:mt-28 lg:mt-[15rem] lg:gap-x-20 
        xl:mt-[19rem] xl:gap-x-24 min-[1440px]:mt-[26rem] 2xl:mt-[25rem] min-[2560px]:mt-[29rem]">
          {projectHighlights.map((highlight, idx) => (
            <motion.article
              key={highlight.number}
              className="relative isolate cursor-pointer md:mt-30 lg:ml-10 xl:ml-30"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.25 }}
              whileHover="hover"
              animate="rest"
            >
              {/* ── Number image: absolute on md+, hidden on mobile ── */}
              <motion.img
                initial={{ opacity: 0, y: 16, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: false, amount: 0.25 }}
                transition={{
                  opacity: { duration: 0.55, delay: idx * 0.42 + 0.08, ease: [0.22, 1, 0.36, 1] },
                  y: { duration: 0.55, delay: idx * 0.42 + 0.08, ease: [0.22, 1, 0.36, 1] },
                  scale: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
                }}
                variants={{
                  rest: { y: 0 },
                  hover: { y: 15 },
                }}
                animate={{ scale: [1, 1.1, 1] }}
                src={cryptonaryNumberImages[highlight.number]}
                alt=""
                aria-hidden
                className="pointer-events-none select-none object-contain opacity-65
                  hidden
                  md:absolute md:block md:-left-12 md:-top-[5.2rem] md:z-0 md:h-[6rem] md:w-[6rem]
                  lg:-left-14 lg:-top-[5.1rem] lg:h-[8.5rem] lg:w-[8.5rem]
                  min-[1440px]:-top-[4.8rem]
                  min-[2560px]:-top-[4.5rem]"
              />

              {/* ── Content ── */}
              <motion.div
                className="relative z-10"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: false, amount: 0.25 }}
                transition={{ duration: 0.5, delay: idx * 0.42 + 0.2, ease: [0.22, 1, 0.36, 1] }}
                variants={{
                  rest: { y: 0 },
                  hover: { y: -10 },
                }}
              >
                {/* Mobile: number image left | heading+points right */}
                {/* Tablet+: original stacked layout                  */}
                <div className="flex flex-col items-start text-start gap-3 md:block">

                  {/* Number image — inline left on mobile only */}
                  <motion.img
                    initial={{ opacity: 0, y: 16, scale: 0.96 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: false, amount: 0.25 }}
                    transition={{
                      opacity: { duration: 0.55, delay: idx * 0.42 + 0.08, ease: [0.22, 1, 0.36, 1] },
                      y: { duration: 0.55, delay: idx * 0.42 + 0.08, ease: [0.22, 1, 0.36, 1] },
                      scale: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
                    }}
                    animate={{ scale: [1, 1.1, 1] }}
                    src={cryptonaryNumberImages[highlight.number]}
                    alt=""
                    aria-hidden
                    className="pointer-events-none select-none object-contain opacity-65 shrink-0
block w-[3rem] h-[3rem]
mb-2
md:hidden"
                  />

                  {/* Right side: heading + points */}
                  <div className="w-full md:block">
                    <motion.h2
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: false, amount: 0.25 }}
                      transition={{ duration: 0.45, delay: idx * 0.42 + 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="font-rajdhani font-semibold text-[1.5rem] md:text-[2rem] leading-[1.02] tracking-[-0.01em] text-white lg:text-[2.15rem]"
                    >
                      {highlight.title}
                    </motion.h2>

                    <ul className="mt-2 md:mt-4 text-start space-y-2 md:space-y-2.5 opacity-80">
                      {highlight.items.map((item, itemIndex) => (
                        <motion.li
                          key={item}
                          initial={{ opacity: 0, y: 10 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: false, amount: 0.25 }}
                          transition={{ duration: 0.4, delay: idx * 0.42 + 0.38 + itemIndex * 0.08, ease: [0.22, 1, 0.36, 1] }}
                          className="flex items-start gap-2.5 font-gilroy-medium text-[15px] md:text-[1.2rem] leading-[1.35] lg:text-[1.3rem]"
                          style={{ color: colors.cryptonaryTextHeroBody }}
                        >
                          <span
                            className="mt-1.5 md:mt-2 h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{ backgroundColor: colors.cryptonaryTextHeroBody }}
                          />
                          <span>{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                </div>
              </motion.div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;